import { format } from 'date-fns';
import type { ScheduleCampaignRestInput } from '../types/campaigns-rest.types';

const WEEKDAY_API = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;

/** Wizard floor is 6 minutes so a slow submit stays above the API's 5-minute rule. */
export const MIN_SCHEDULE_AHEAD_MS = 6 * 60_000;

export type ScheduleWizardFrequency = 'Daily' | 'Weekly' | 'Monthly' | 'Yearly';

export type BuildScheduleCampaignInput = {
  campaignId: string;
  idempotencyKey: string;
  sendType: 'Scheduled' | 'Recurring';
  scheduleDate: Date;
  scheduleTime: string;
  timeZone: string;
  recurringFrequency: ScheduleWizardFrequency;
  recurringStartDate: Date;
  throttlePerHour?: number | null;
};

/**
 * Interpret calendar date + HH:mm as wall clock in `timeZone`, return UTC ISO-8601.
 */
export const wallClockInZoneToUtcIso = (
  date: Date,
  timeHHmm: string,
  timeZone: string
): string => {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const [hourRaw, minuteRaw] = (timeHHmm || '00:00').split(':');
  const hour = Number(hourRaw);
  const minute = Number(minuteRaw);
  if (
    !Number.isFinite(hour) ||
    !Number.isFinite(minute) ||
    hour < 0 ||
    hour > 23 ||
    minute < 0 ||
    minute > 59
  ) {
    throw new Error('Invalid schedule time');
  }

  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });

  const readZoneParts = (ms: number) => {
    const parts = formatter.formatToParts(new Date(ms));
    const get = (type: Intl.DateTimeFormatPartTypes) =>
      Number(parts.find((part) => part.type === type)?.value);
    let h = get('hour');
    if (h === 24) h = 0;
    return {
      year: get('year'),
      month: get('month'),
      day: get('day'),
      hour: h,
      minute: get('minute'),
    };
  };

  // Start from the same wall clock treated as UTC, then correct by zone offset.
  let guess = Date.UTC(year, month - 1, day, hour, minute, 0);
  for (let i = 0; i < 4; i += 1) {
    const seen = readZoneParts(guess);
    const seenAsUtc = Date.UTC(seen.year, seen.month - 1, seen.day, seen.hour, seen.minute, 0);
    const desiredAsUtc = Date.UTC(year, month - 1, day, hour, minute, 0);
    const delta = desiredAsUtc - seenAsUtc;
    if (delta === 0) break;
    guess += delta;
  }

  return new Date(guess).toISOString();
};

/** Inverse of {@link wallClockInZoneToUtcIso}: the `yyyy-MM-dd` / `HH:mm` an instant reads as in `timeZone`. */
export const utcIsoToWallClockInZone = (
  iso: string,
  timeZone: string
): { date: string; time: string } | null => {
  const instant = new Date(iso);
  if (Number.isNaN(instant.getTime())) return null;

  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(instant);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '';
  const hour = get('hour') === '24' ? '00' : get('hour');

  return { date: `${get('year')}-${get('month')}-${get('day')}`, time: `${hour}:${get('minute')}` };
};

const mapFrequency = (
  frequency: ScheduleWizardFrequency
): { frequency: 'daily' | 'weekly' | 'monthly'; interval: number } => {
  switch (frequency) {
    case 'Daily':
      return { frequency: 'daily', interval: 1 };
    case 'Weekly':
      return { frequency: 'weekly', interval: 1 };
    case 'Monthly':
      return { frequency: 'monthly', interval: 1 };
    case 'Yearly':
      // API has no yearly — approximate as every 12 months (guide §11 gap #2).
      return { frequency: 'monthly', interval: 12 };
    default:
      return { frequency: 'weekly', interval: 1 };
  }
};

/** Inverse of {@link mapFrequency}: monthly every 12 is the wizard's "Yearly". */
export const mapApiFrequency = (
  frequency: string | null | undefined,
  interval: number | null | undefined
): ScheduleWizardFrequency => {
  switch (frequency?.trim().toLowerCase()) {
    case 'daily':
      return 'Daily';
    case 'monthly':
      return interval === 12 ? 'Yearly' : 'Monthly';
    default:
      return 'Weekly';
  }
};

/** The recurrence the wizard's recurring options describe — shared by the schedule and draft-schedule bodies. */
export const buildScheduleRecurrence = (
  recurringFrequency: ScheduleWizardFrequency,
  recurringStartDate: Date,
  timeOfDay: string,
  timeZone: string
): NonNullable<ScheduleCampaignRestInput['recurrence']> => {
  const { frequency, interval } = mapFrequency(recurringFrequency);
  const recurrence: NonNullable<ScheduleCampaignRestInput['recurrence']> = {
    frequency,
    interval,
    startDate: format(recurringStartDate, 'yyyy-MM-dd'),
    timeOfDay,
    timeZone,
  };

  if (frequency === 'weekly') {
    recurrence.byWeekday = [WEEKDAY_API[recurringStartDate.getDay()] ?? 'mon'];
  }

  return recurrence;
};

/** Build POST …/schedule body from wizard schedule step state (guide §5 / §11 gap #2). */
export const buildScheduleCampaignRestInput = (
  input: BuildScheduleCampaignInput
): ScheduleCampaignRestInput => {
  const campaignId = input.campaignId.trim();
  const idempotencyKey = input.idempotencyKey.trim();
  const timeZone = input.timeZone.trim() || 'UTC';
  const timeOfDay = (input.scheduleTime || '00:00').trim();

  if (input.sendType === 'Scheduled') {
    return {
      campaignId,
      idempotencyKey,
      mode: 'scheduled',
      scheduledAt: wallClockInZoneToUtcIso(input.scheduleDate, timeOfDay, timeZone),
      timeZone,
      throttlePerHour: input.throttlePerHour ?? undefined,
    };
  }

  const recurrence = buildScheduleRecurrence(
    input.recurringFrequency,
    input.recurringStartDate,
    timeOfDay,
    timeZone
  );

  return {
    campaignId,
    idempotencyKey,
    mode: 'recurring',
    throttlePerHour: input.throttlePerHour ?? undefined,
    recurrence,
  };
};
